import { useCallback } from 'react';
import { useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { canSignNow } from '../site_document_details.helpers';
const SIGNER_APP_LINK_ROUTE = '/v2/routes/econsent/get_signer_app_link';
/**
 * Hands the device to a signer who is present in person: resolves that invite's signer
 * app link, signs the site user out, and sends a new tab to the signer app.
 *
 * The window is opened *before* the await chain because browsers only honour
 * `window.open` inside the click gesture; opening it after the request resolves gets it
 * blocked as a popup. Logout is deliberately last before the redirect, so a failure to
 * resolve the link leaves the site user's session intact. The site user's own tab stays
 * on the site app, as it did in classic — only a blocked popup falls back to it.
 */
export const useSignNowAction = ({ apiClient, orgCode, currentUserEmail, onLogout, getApiErrorMessage, }) => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const canSignNowFor = useCallback((row, rows) => 
    // No logout available means no way to hand over safely, so the action is hidden
    // rather than offered and half-working.
    !!onLogout && !!apiClient && canSignNow(row, rows, currentUserEmail), [apiClient, currentUserEmail, onLogout]);
    const signNow = useCallback(async (row) => {
        if (!apiClient || !onLogout) {
            return;
        }
        // Opened without a features string on purpose: passing `noopener` makes window.open
        // return null by specification, which would leave no handle to redirect and would
        // send the site user's own tab to the signer app. The opener reference is severed
        // straight afterwards, which gives the same protection while keeping the handle.
        const signerWindow = window.open('', '_blank');
        if (signerWindow) {
            signerWindow.opener = null;
        }
        try {
            const response = await apiClient.get(`${SIGNER_APP_LINK_ROUTE}/${row.id}`);
            const signerAppLink = response?.data?.signerAppLink;
            if (!signerAppLink) {
                throw new Error('Failed to obtain signer link');
            }
            await onLogout();
            const target = orgCode ? `${signerAppLink}?org=${encodeURIComponent(orgCode)}` : signerAppLink;
            if (signerWindow) {
                signerWindow.location.href = target;
            }
            else {
                // Popup blocked: fall back to the current tab, which is acceptable now that
                // the session has been ended anyway.
                window.location.href = target;
            }
        }
        catch (error) {
            signerWindow?.close();
            addNotification({
                title: '',
                subtitle: getApiErrorMessage?.(error) ?? translate('error.site_document_details.signer_link_failed'),
                type: 'error',
            });
        }
    }, [addNotification, apiClient, getApiErrorMessage, onLogout, orgCode, translate]);
    return { canSignNowFor, signNow };
};
